import { useGetRequest } from "@acin.reactjs/acin-ui/axios-backend-provider";
import { CloseIcon } from "@acin.reactjs/acin-ui/icons-misc";
import NotificationBars from "@acin.reactjs/acin-ui/notificationBar";
import { useCallback, useMemo, useRef } from "react";
import styled from "styled-components";
import { applicationAlertTypesIds } from "./constants/AppConstants";
import {
  ApplicationAlertResponse,
  CollectionResponse,
} from "./interfaces/BackendInterfaces";
import { containsHtml } from "./utils/Utils";

const NotificationContent = styled.div({
  "& > *": {
    margin: 0,
  },

  "& p": {
    margin: 0,
  },

  "& ul, & ol": {
    margin: 0,
    paddingLeft: "16px",
  },

  "& li": {
    margin: 0,
  },

  "& h1, & h2, & h3, & h4, & h5, & h6": {
    margin: 0,
    fontSize: "inherit",
  },
});

type NotificationBarsComponentProps = {
  isPrivate: boolean;
};

const NotificationBarsComponent = ({
  isPrivate,
}: NotificationBarsComponentProps) => {
  const { data: applicationAlerts } =
    useGetRequest<CollectionResponse<ApplicationAlertResponse>>(
      "application-alerts",
    );

  const containerRef = useRef<HTMLDivElement>(null);

  const onClickClose = useCallback(() => true, []);

  const shouldShowNotification = useCallback(
    (areaId: number, privateFlag: boolean) => {
      switch (areaId) {
        case 1:
          return true;
        case 2:
          return !privateFlag;
        case 3:
          return privateFlag;
        default:
          return false;
      }
    },
    [],
  );

  const notificationBars = useMemo(
    () =>
      applicationAlerts?.data
        ?.filter(({ startAt, endAt, area }) => {
          if (!area || typeof area.id !== "number") return false;

          const now = new Date();
          const isInDateRange =
            new Date(startAt) <= now && new Date(endAt) >= now;

          return isInDateRange && shouldShowNotification(area.id, isPrivate);
        })
        .map(({ id, description, type }) => ({
          type:
            applicationAlertTypesIds.warning === type.id ? "warning" : "info",
          notificationBarId: id.toString(),
          content: containsHtml(description) ? (
            <NotificationContent
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: description }}
            />
          ) : (
            description
          ),
          closeButton: { text: <CloseIcon />, onClick: onClickClose },
        })),
    [applicationAlerts?.data, isPrivate, shouldShowNotification, onClickClose],
  );

  if (!notificationBars?.length) return null;

  return (
    <div id="notification-bars" ref={containerRef}>
      <NotificationBars notificationBars={notificationBars} />
    </div>
  );
};

export default NotificationBarsComponent;
